import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { isToday } from 'date-fns';
import { noop, useNoticeBannersStackContext } from '@sg/garnish';
import { type DeliveryOrderDetail } from '@sg/graphql-schema';

import {
  adjustWantedTime,
  type OrderingContext,
} from '@order/features/ordering';
import { type PartialLineItem } from '@order/graphql';
import { ignoreTimezone } from '@order/utils';

/**
 * This allows for a {orderSubmissionRef} to receive the response from the order submission flow without an useEffect.
 * Handles other events post order submission that requires access to the context.
 */
export const useOrderSubmissionResponseHandler = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const orderSubmissionRef = useRef<(success: boolean) => void>(noop);

  const handleOrderSubmissionResponse = useCallback(
    (context: Context, event: OrderSubmissionEvent) => {
      const isTimeslotUnavailable = event?.data?.isTimeslotUnavailable;
      const isNextTimeslotTomorrow = checkIsNextTimeslotInFuture(context);

      if (isTimeslotUnavailable && !isNextTimeslotTomorrow) {
        addNoticeBanner({
          text: formatMessage(messages.timeslotUnavailable),
          palette: 'neutral',
        });
      }

      orderSubmissionRef.current?.(Boolean(event?.data?.success));
    },
    [addNoticeBanner, formatMessage],
  );

  return { orderSubmissionRef, handleOrderSubmissionResponse };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  timeslotUnavailable: {
    defaultMessage:
      'The selected timeslot window is no longer available. Your order has been updated to the next available time.',
    description: 'Bag | Order submission error | Timeslot unavailable',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const checkIsNextTimeslotInFuture = (context: Context) => {
  const nextTimeslot = context.cart.wantedTimes[0];
  const adjustedTime = adjustWantedTime(nextTimeslot);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;

  return Boolean(validTime && !isToday(validTime));
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Context = OrderingContext<PartialLineItem, DeliveryOrderDetail>;
type OrderSubmissionEvent = {
  data: {
    success: boolean;
    isTimeslotUnavailable: boolean;
  };
};
